import { useCallback, useEffect, useState } from 'react';
import { useAutoSave } from '@/shared/hooks/useAutoSave';
import { messageFromError } from '@/shared/api/apiError';
import {
  CertificateRequest,
  Option,
  Profile,
} from '@/shared/types/certificate-request';
import { certificateRequestApi } from '../api/certificateRequestApi';
import { contactApi } from '../api/contactApi';
import { profileApi } from '../api/profileApi';
import { referenceDataApi } from '../api/referenceDataApi';
import { IncidentFormValue, toDraftInput, toFormValue } from '../incidentForm';

export type StageKey = 'incident' | 'personal' | 'contact' | 'review';

/**
 * Todo o estado e as chamadas do fluxo de solicitação.
 *
 * Fica separado da página de propósito: a página só escolhe qual etapa
 * desenhar, e as etapas são componentes de apresentação, sem saber que existe
 * uma API do outro lado. Assim dá para testar cada peça isolada.
 */
export function useRequestWizard() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * As etapas desta solicitação são decididas uma vez, na abertura, e não se
   * recalculam durante a sessão. Se recalculássemos a cada mudança de perfil
   * (ex.: assim que "Seus dados" é salvo), o array encolheria no meio do
   * fluxo e o índice da etapa atual passaria a apontar para outra coisa —
   * foi assim que o step de Contato voltava a aparecer depois de já
   * confirmado. Fixando o array, "Contato" simplesmente nunca existe nele
   * quando o e-mail já está confirmado, e nenhuma navegação (avançar,
   * voltar, ou clicar num passo já concluído) consegue revelá-lo.
   */
  const [flowStages, setFlowStages] = useState<StageKey[]>(['incident', 'review']);
  const [stageIndex, setStageIndex] = useState(0);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [request, setRequest] = useState<CertificateRequest | null>(null);
  const [incidentTypes, setIncidentTypes] = useState<Option[]>([]);
  const [incidentForm, setIncidentForm] = useState<IncidentFormValue | null>(null);
  const [codeRecipient, setCodeRecipient] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<CertificateRequest | null>(null);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        // Perfil antes do rascunho, em série: as duas chamadas criam o
        // solicitante no primeiro acesso, e disparadas juntas competiriam pelo
        // mesmo INSERT. As referências não dependem de nada e vão junto.
        const [referenceData, currentProfile] = await Promise.all([
          referenceDataApi.load(),
          profileApi.get(),
        ]);
        const draft = await certificateRequestApi.openDraft();

        if (!active) return;

        const stages: StageKey[] = ['incident'];
        if (!currentProfile.profileComplete) stages.push('personal');
        if (!currentProfile.emailConfirmed) stages.push('contact');
        stages.push('review');

        setFlowStages(stages);
        setIncidentTypes(referenceData.incidentTypes);
        setProfile(currentProfile);
        setRequest(draft);
        setIncidentForm(toFormValue(draft));
      } catch (error) {
        if (active) {
          setLoadError(messageFromError(error, 'Não conseguimos abrir a solicitação.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const persistDraft = useCallback(
    async (value: IncidentFormValue) => {
      if (!request) return;
      setRequest(await certificateRequestApi.saveDraft(request.id, toDraftInput(value)));
    },
    [request],
  );

  const autoSave = useAutoSave(persistDraft);

  const changeIncidentForm = useCallback(
    (value: IncidentFormValue) => {
      setIncidentForm(value);
      autoSave.schedule(value);
    },
    [autoSave],
  );

  /** Só permite ir direto para uma etapa que faz parte do fluxo desta solicitação. */
  const goToStage = useCallback(
    (key: StageKey) => {
      setStepError(null);
      setStageIndex((current) => {
        const index = flowStages.indexOf(key);
        return index >= 0 ? index : current;
      });
    },
    [flowStages],
  );

  const next = useCallback(() => {
    setStepError(null);
    setStageIndex((current) => Math.min(current + 1, flowStages.length - 1));
  }, [flowStages]);

  const back = useCallback(() => {
    setStepError(null);
    setStageIndex((current) => Math.max(current - 1, 0));
  }, []);

  const leaveIncidentStep = useCallback(async () => {
    await autoSave.flush();
    next();
  }, [autoSave, next]);

  /** Envolve uma chamada com estado de ocupado e tradução de erro. */
  const run = useCallback(
    async <T,>(action: () => Promise<T>, onSuccess: (result: T) => void) => {
      setStepError(null);
      setBusy(true);
      try {
        onSuccess(await action());
      } catch (error) {
        setStepError(messageFromError(error));
        throw error;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const savePersonalData = useCallback(
    async (input: Parameters<typeof profileApi.savePersonalData>[0]) => {
      await run(
        () => profileApi.savePersonalData(input),
        (updated) => {
          setProfile(updated);
          next();
        },
      ).catch(() => undefined);
    },
    [next, run],
  );

  const saveContact = useCallback(
    async (input: Parameters<typeof profileApi.saveContact>[0]) => {
      await run(() => profileApi.saveContact(input), setProfile);
    },
    [run],
  );

  const sendVerificationCode = useCallback(async () => {
    return run(
      () => contactApi.sendVerificationCode(),
      (result) => {
        setCodeRecipient(result.recipient);
      },
    )
      .then(() => true)
      .catch(() => false);
  }, [run]);

  const verifyCode = useCallback(
    async (code: string) => {
      await run(
        () => contactApi.verifyCode(code),
        (updated) => {
          setProfile(updated);
          setCodeRecipient(null);
        },
      ).catch(() => undefined);
    },
    [run],
  );

  const submit = useCallback(async () => {
    if (!request) return;
    await run(() => certificateRequestApi.submit(request.id), setSubmitted).catch(
      () => undefined,
    );
  }, [request, run]);

  return {
    loading,
    loadError,
    stepError,
    busy,
    flowStages,
    stageIndex,
    profile,
    request,
    incidentTypes,
    incidentForm,
    autoSaveState: autoSave.state,
    codeRecipient,
    submitted,
    goToStage,
    next,
    back,
    changeIncidentForm,
    leaveIncidentStep,
    savePersonalData,
    saveContact,
    sendVerificationCode,
    verifyCode,
    submit,
  };
}
